import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faPaperPlane, faFlag, faBan } from "@fortawesome/free-solid-svg-icons";
import { onSocketEvent, offSocketEvent } from "../../services/socket";
import "./inquiryThread.css";

function formatTime(dateStr) {
  return new Date(dateStr).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

// Full-page view of a 1:1 pre-booking inquiry thread. Reachable from the inbox
// or straight after tapping "Message the driver" on a ride.
const InquiryThread = ({ isLoggedIn, loginUser, onLogout }) => {
  const { inquiryId } = useParams();
  const navigate = useNavigate();
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [reportReason, setReportReason] = useState("harassment");
  const [reportDescription, setReportDescription] = useState("");
  const endRef = useRef(null);

  const markRead = useCallback(async () => {
    try {
      await axios.put(`/api/v1/inquiries/${inquiryId}/read`);
    } catch (e) {
      // Silent — read receipts are non-critical
    }
  }, [inquiryId]);

  const fetchThread = useCallback(async () => {
    try {
      const { data } = await axios.get(`/api/v1/inquiries/${inquiryId}`);
      setThread(data.thread);
      setMessages(data.messages);
      markRead();
    } catch (err) {
      if (err.response && err.response.status === 401) {
        navigate("/login");
        return;
      }
      if (err.response && (err.response.status === 404 || err.response.status === 403)) {
        setNotFound(true);
      }
    } finally {
      setLoading(false);
    }
  }, [inquiryId, navigate, markRead]);

  useEffect(() => {
    fetchThread();
  }, [fetchThread]);

  // Live: a new message on any inquiry arrives as a personal notification.
  useEffect(() => {
    const handler = (n) => {
      if (n.type === "inquiry_message") fetchThread();
    };
    onSocketEvent("notification", handler);
    return () => offSocketEvent("notification", handler);
  }, [fetchThread]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  if (!isLoggedIn) {
    navigate("/");
    return null;
  }

  const iAmDriver = thread?.role === "driver";
  const blockedForMe = thread?.blockedByDriver && !iAmDriver;

  const handleSend = async (e) => {
    e.preventDefault();
    const content = input.trim();
    if (!content || sending) return;

    const tempMsg = {
      _id: "temp-" + Date.now(),
      sender: { _id: loginUser._id, userName: loginUser.userName, avatar: loginUser.avatar },
      content,
      createdAt: new Date().toISOString(),
    };
    setMessages((p) => [...p, tempMsg]);
    setInput("");
    setSending(true);
    try {
      const { data } = await axios.post(`/api/v1/inquiries/${inquiryId}/messages`, { content });
      setMessages((p) => p.map((m) => (m._id === tempMsg._id ? data.message : m)));
    } catch (err) {
      setMessages((p) => p.filter((m) => m._id !== tempMsg._id));
      toast.error(err.response?.data?.message || "Couldn't send.");
    } finally {
      setSending(false);
    }
  };

  const toggleBlock = async () => {
    try {
      const { data } = await axios.put(`/api/v1/inquiries/${inquiryId}/block`, {
        blocked: !thread.blockedByDriver,
      });
      setThread((t) => ({ ...t, blockedByDriver: data.blockedByDriver }));
      toast.success(
        data.blockedByDriver ? "Blocked. They can no longer message you." : "Unblocked."
      );
    } catch (err) {
      toast.error("Couldn't update.");
    }
  };

  const submitReport = async () => {
    try {
      await axios.post("/api/v1/report/create", {
        reportedUser: thread.otherParty._id,
        reportedRide: thread.rideId,
        reason: reportReason,
        description: reportDescription,
      });
      toast.success("Report submitted. Thank you.");
      setShowReport(false);
      setReportDescription("");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to submit report.");
    }
  };

  if (loading) {
    return (
      <div className="sr-inqt">
        <div className="sr-inqt__notice">Loading…</div>
      </div>
    );
  }
  if (notFound || !thread) {
    return (
      <div className="sr-inqt">
        <div className="sr-inqt__notice">This conversation isn't available.</div>
      </div>
    );
  }

  return (
    <div className="sr-inqt">
      <div className="sr-inqt__header">
        <button className="sr-inqt__back" onClick={() => navigate("/inbox")} aria-label="Back to inbox">
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        {thread.otherParty.avatar && (
          <img className="sr-inqt__avatar" src={thread.otherParty.avatar} alt="" />
        )}
        <div className="sr-inqt__peer">
          <div className="sr-inqt__peer-name">{thread.otherParty.userName}</div>
          {thread.ride && (
            <button className="sr-inqt__ride-link" onClick={() => navigate(`/ride/${thread.rideId}`)}>
              {thread.ride.departure} → {thread.ride.arrival}
            </button>
          )}
        </div>
        <div className="sr-inqt__header-actions">
          {iAmDriver && (
            <button
              className={`sr-inqt__icon-btn${thread.blockedByDriver ? " is-active" : ""}`}
              onClick={toggleBlock}
              title={thread.blockedByDriver ? "Unblock" : "Block"}
            >
              <FontAwesomeIcon icon={faBan} />
            </button>
          )}
          <button className="sr-inqt__icon-btn" onClick={() => setShowReport((s) => !s)} title="Report">
            <FontAwesomeIcon icon={faFlag} />
          </button>
        </div>
      </div>

      {showReport && (
        <div className="sr-inqt__report">
          <select value={reportReason} onChange={(e) => setReportReason(e.target.value)}>
            <option value="spam">Spam</option>
            <option value="harassment">Harassment</option>
            <option value="inappropriate">Inappropriate</option>
            <option value="fraud">Fraud</option>
            <option value="safety">Safety Concern</option>
            <option value="other">Other</option>
          </select>
          <textarea
            value={reportDescription}
            onChange={(e) => setReportDescription(e.target.value)}
            placeholder="Describe the issue (optional)"
            maxLength={1000}
          />
          <button onClick={submitReport}>Submit Report</button>
        </div>
      )}

      <div className="sr-inqt__messages">
        {messages.length === 0 ? (
          <p className="sr-inqt__empty">Say hello — ask about seats, pickup point, or timing.</p>
        ) : (
          messages.map((msg) => {
            const isOwn = msg.sender._id === loginUser._id;
            return (
              <div
                key={msg._id}
                className={`sr-inqt__msg ${isOwn ? "sr-inqt__msg--own" : "sr-inqt__msg--other"}`}
              >
                {!isOwn && msg.sender.avatar && (
                  <img src={msg.sender.avatar} alt="" className="sr-inqt__msg-avatar" />
                )}
                <div>
                  <div className="sr-inqt__msg-bubble">{msg.content}</div>
                  <div className="sr-inqt__msg-time">{formatTime(msg.createdAt)}</div>
                </div>
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </div>

      {blockedForMe ? (
        <div className="sr-inqt__blocked-note">You can no longer message this driver.</div>
      ) : (
        <form className="sr-inqt__composer" onSubmit={handleSend}>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type a message…"
            maxLength={1000}
          />
          <button type="submit" disabled={!input.trim() || sending} aria-label="Send message">
            <FontAwesomeIcon icon={faPaperPlane} />
          </button>
        </form>
      )}
    </div>
  );
};

export default InquiryThread;
